import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { CONTACT, SERVICES } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  phone: "",
  email: "",
  service_required: "",
  message: "",
};

export default function ContactSection({ headingOverride }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please fill in required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Our team will reach out within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="contact-section" className="py-24 lg:py-32 bg-white relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="kicker">Talk To Us</div>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl font-black text-navy tracking-tight leading-tight">
            {headingOverride || (
              <>Let{`\u2019`}s build a <span className="text-safety">safer</span> workplace.</>
            )}
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Share your requirement — audit, assessment, certification or advisory —
            and our team will respond within 24 hours.
          </p>

          <div className="mt-10 space-y-5">
            <ContactRow
              icon={Phone}
              label="Call us"
              value={`+91 ${CONTACT.phone}`}
              href={`tel:+91${CONTACT.phone}`}
              testid="contact-phone"
            />
            <ContactRow
              icon={Mail}
              label="Email"
              value={CONTACT.email}
              href={`mailto:${CONTACT.email}`}
              testid="contact-email"
            />
            <ContactRow icon={MapPin} label="Location" value={CONTACT.address} testid="contact-address" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="bg-[#F4F7FA] border border-navy/10 rounded-2xl p-8 lg:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full Name *" value={form.name} onChange={update("name")} testid="field-name" required />
              <Field label="Company" value={form.company} onChange={update("company")} testid="field-company" />
              <Field label="Phone *" type="tel" value={form.phone} onChange={update("phone")} testid="field-phone" required />
              <Field label="Email *" type="email" value={form.email} onChange={update("email")} testid="field-email" required />
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Service Required</label>
                <select
                  data-testid="field-service"
                  value={form.service_required}
                  onChange={update("service_required")}
                  className="mt-2 w-full bg-white border border-navy/15 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-safety transition"
                >
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.title}>{s.title}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Message *</label>
                <textarea
                  data-testid="field-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your facility, scope and timelines…"
                  className="mt-2 w-full bg-white border border-navy/15 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-safety transition resize-none"
                />
              </div>
            </div>
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex items-center gap-2 bg-navy text-white px-7 py-4 rounded-full font-medium btn-lift hover:bg-safety disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send Message <Send className="h-4 w-4" />
                </>
              )}
            </button>
            <p className="mt-3 text-xs text-slate-500">By submitting, you agree to be contacted by Insafe Services regarding your enquiry.</p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-2 w-full bg-white border border-navy/15 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-safety transition"
      />
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-center gap-4 group">
      <div className="h-12 w-12 rounded-full bg-navy text-white flex items-center justify-center group-hover:bg-safety transition-colors flex-shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-heading text-lg font-bold text-navy mt-0.5">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block">{inner}</a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
